"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  fetchAnalysisStatus,
  queryAnalysisDependencies,
  queryAnalysisDocumentLinks,
  triggerNamespaceAnalysis,
} from "@/lib/api-client";
import { queryKeys } from "@/lib/query/keys";
import type { NamespaceId } from "@/types/namespaces";
import type {
  AnalysisDependencyQueryRequest,
  AnalysisDependencyQueryResponse,
  AnalysisDocumentLinksResponse,
  AnalysisRunStatusResponse,
} from "@/types/analysis";

const DISABLED = "__analysis-disabled__" as NamespaceId;

/** Poll cadence while a run is Pending or Running: progress moves by the second. */
export const ACTIVE_RUN_POLL_MS = 5_000;
/**
 * Poll cadence while no run is active. Not "stop": runs start on every push
 * and on a schedule, with nothing on the client to notice. With
 * refetch-on-focus off app-wide, a query that stopped polling at Idle never
 * saw the next run, so the top-bar indicator stayed blank until a reload.
 * The endpoint is one row read, so a slow heartbeat is cheap.
 */
export const IDLE_RUN_POLL_MS = 30_000;

/** How often to re-read the run status given the last answer: fast while a run is active, slow otherwise. */
export function analysisStatusPollInterval(data: AnalysisRunStatusResponse | undefined): number {
  if (!data) return IDLE_RUN_POLL_MS;
  return data.status === "Pending" || data.status === "Running" ? ACTIVE_RUN_POLL_MS : IDLE_RUN_POLL_MS;
}

export function useAnalysisRunStatusQuery(namespaceId?: NamespaceId) {
  const key = namespaceId ?? DISABLED;
  return useQuery<AnalysisRunStatusResponse>({
    queryKey: queryKeys.analysisStatus(key),
    queryFn: () => {
      if (!namespaceId) throw new Error("Namespace ID required");
      return fetchAnalysisStatus(namespaceId);
    },
    enabled: Boolean(namespaceId),
    refetchInterval: (query) => analysisStatusPollInterval(query.state.data),
    refetchOnWindowFocus: true,
  });
}

export function useAnalysisDependenciesQuery(
  namespaceId: NamespaceId | undefined,
  request: Omit<AnalysisDependencyQueryRequest, "signal"> | null,
) {
  const enabled = Boolean(namespaceId && request?.target);
  return useQuery<AnalysisDependencyQueryResponse>({
    queryKey: queryKeys.analysisDependencies(
      namespaceId ?? DISABLED,
      request ?? { queryType: "affected_by_file", target: "" },
    ),
    enabled,
    queryFn: ({ signal }) => {
      if (!namespaceId || !request) {
        throw new Error("Namespace ID and request required");
      }
      return queryAnalysisDependencies(namespaceId, { ...request, signal });
    },
    staleTime: 30_000,
  });
}

export function useAnalysisDocumentLinksQuery(
  namespaceId: NamespaceId | undefined,
  filePath: string | null | undefined,
) {
  const enabled = Boolean(namespaceId && filePath);
  return useQuery<AnalysisDocumentLinksResponse>({
    queryKey: queryKeys.analysisDocumentLinks(namespaceId ?? DISABLED, filePath ?? ""),
    enabled,
    queryFn: ({ signal }) => {
      if (!namespaceId || !filePath) {
        throw new Error("Namespace ID and filePath required");
      }
      return queryAnalysisDocumentLinks(namespaceId, { filePath, signal });
    },
    staleTime: 30_000,
  });
}

export function useTriggerNamespaceAnalysisMutation(namespaceId?: NamespaceId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (options?: { incremental?: boolean }) => {
      if (!namespaceId) throw new Error("Namespace ID required to start learning");
      return triggerNamespaceAnalysis(namespaceId, options);
    },
    onSuccess: () => {
      if (!namespaceId) return;
      queryClient.invalidateQueries({ queryKey: queryKeys.analysisStatus(namespaceId) });
    },
  });
}
